import { useMemo } from "react";

import { useTrustpilotRating } from "@/app/components/cover-type/components/description-section/trust-pilot/trust-pilot.hook";
import {
  formatTrustpilotRating,
  formatTrustpilotReviewCount,
} from "@/app/components/cover-type/utils/cover-type.utils";
import { PRODUCT_DOMAIN } from "@/constants";

type ProductDomainKey = "STS" | "AVN" | "EXP" | "IRE" | "ESP";

const trustPilotProductMap: Record<ProductDomainKey, string> = {
  STS: "www.staysure.co.uk",
  AVN: "www.avantitravelinsurance.co.uk",
  EXP: "staysure.com",
  IRE: "staysure.com/ie",
  ESP: "staysure.com/es",
};

export function useDynamicTrustpilotText(
  text: string | undefined | null,
  brandDomain?: string
): string {
  // Determine brand domain from PRODUCT_DOMAIN if not provided
  const domain =
    brandDomain ||
    trustPilotProductMap[PRODUCT_DOMAIN as ProductDomainKey] ||
    "www.staysure.co.uk";

  // Fetch live Trustpilot data
  const reviewData = useTrustpilotRating(domain);

  // Replace placeholders with dynamic values
  const dynamicText = useMemo(() => {
    if (!text) return "";

    // Check if content contains placeholders
    const hasRatingPlaceholder = !!text && text.includes("[TRUSTPILOT_RATING]");
    const hasReviewCountPlaceholder =
      !!text && text.includes("[TRUSTPILOT_REVIEW_COUNT]");
    const hasPlaceholders = hasRatingPlaceholder || hasReviewCountPlaceholder;

    // If content has placeholders but data isn't loaded yet, return empty string to avoid showing incomplete information
    if (hasPlaceholders && (!reviewData?.score || !reviewData?.totalReviews)) {
      return "";
    }

    let result = text;

    // Replace rating placeholder if valid data exists
    if (reviewData?.score && reviewData?.score > 0) {
      result = result.replace(
        "[TRUSTPILOT_RATING]",
        formatTrustpilotRating(reviewData?.score)
      );
    }

    // Replace review count placeholder if valid data exists
    if (reviewData?.totalReviews) {
      result = result.replace(
        "[TRUSTPILOT_REVIEW_COUNT]",
        formatTrustpilotReviewCount(reviewData.totalReviews)
      );
    }

    return result;
  }, [text, reviewData?.score, reviewData?.totalReviews]);

  return dynamicText;
}
